/**
 * Universal Date Utilities for YegoNews
 * Handles ISO timestamps with/without offsets (+00:00, Z), Firestore timestamps,
 * SQLite/MySQL timestamps, Unix epochs, and relative time formatting with locale support.
 */

export const parseDate = (raw) => {
  if (!raw) return null;
  
  if (raw instanceof Date) {
    return isNaN(raw.getTime()) ? null : raw;
  }
  
  // Firestore Timestamp object: { seconds: ..., nanoseconds: ... } or with toDate()
  if (typeof raw === 'object') {
    if (typeof raw.toDate === 'function') {
      try {
        const d = raw.toDate();
        if (d instanceof Date && !isNaN(d.getTime())) return d;
      } catch (e) {}
    }
    if (typeof raw._seconds === 'number') {
      return new Date(raw._seconds * 1000);
    }
    if (typeof raw.seconds === 'number') {
      return new Date(raw.seconds * 1000);
    }
  }
  
  // Unix timestamp (number)
  if (typeof raw === 'number') {
    // If seconds (10 digits ~ 1.7e9), convert to milliseconds
    return new Date(raw < 1e11 ? raw * 1000 : raw);
  }
  
  // String parsing
  if (typeof raw === 'string') {
    let s = raw.trim();
    if (!s) return null;

    // 1. Direct parse (works for standard ISO 8601 strings with timezone offset e.g. 2026-02-02T18:25:57.841000+00:00 or with Z)
    let d = new Date(s);
    if (!isNaN(d.getTime())) {
      return d;
    }

    // 2. Space separated datetime e.g. "2026-02-02 18:25:57"
    if (s.includes(' ') && !s.includes('T')) {
      s = s.replace(' ', 'T');
      d = new Date(s);
      if (!isNaN(d.getTime())) return d;
    }

    // 3. If no timezone indicator (no 'Z', no '+', and no '-' after character 10), assume UTC
    if (!s.endsWith('Z') && !s.includes('+') && !s.slice(10).includes('-')) {
      d = new Date(s + 'Z');
      if (!isNaN(d.getTime())) return d;
    }
  }

  // Fallback
  try {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

/**
 * Format relative time ago (e.g., "5m ago", "2h ago", "3d ago", "Feb 2")
 * Supports Kinyarwanda ("5min ishize", "2h ishize", "Ubu nyine")
 */
export const getTimeAgo = (dateInput, isKinyarwanda = false) => {
  const date = parseDate(dateInput);
  if (!date) {
    return isKinyarwanda ? 'Ubu nyine' : 'Just now';
  }

  const now = new Date();
  const diff = now.getTime() - date.getTime();

  // Allow up to 2 minutes clock skew into the future as "Just now"
  if (diff < 60000 && diff > -120000) {
    return isKinyarwanda ? 'Ubu nyine' : 'Just now';
  }

  // If substantially in future (>2 minutes ahead), show the actual date
  if (diff <= -120000) {
    const isDifferentYear = date.getFullYear() !== now.getFullYear();
    const options = { month: 'short', day: 'numeric' };
    if (isDifferentYear) options.year = 'numeric';
    return date.toLocaleDateString(isKinyarwanda ? 'rw-RW' : 'en-US', options);
  }

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (isKinyarwanda) {
    if (minutes < 1) return 'Ubu nyine';
    if (minutes < 60) return `${minutes}min ishize`;
    if (hours < 24) return `${hours}h ishize`;
    if (days < 7) return `${days}d ishize`;
    const isDifferentYear = date.getFullYear() !== now.getFullYear();
    const options = { month: 'short', day: 'numeric' };
    if (isDifferentYear) options.year = 'numeric';
    return date.toLocaleDateString('rw-RW', options);
  }

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  const isDifferentYear = date.getFullYear() !== now.getFullYear();
  const options = { month: 'short', day: 'numeric' };
  if (isDifferentYear) options.year = 'numeric';
  return date.toLocaleDateString('en-US', options);
};

/**
 * Short relative time format (e.g. "5m", "2h", "3d", "Feb 2")
 * Ideal for sidebars and dense lists
 */
export const getTimeAgoShort = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'Just now';

  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000 && diff > -120000) return 'Just now';
  if (diff <= -120000) {
    const isDifferentYear = date.getFullYear() !== now.getFullYear();
    const options = { month: 'short', day: 'numeric' };
    if (isDifferentYear) options.year = 'numeric';
    return date.toLocaleDateString('en-US', options);
  }

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;

  const isDifferentYear = date.getFullYear() !== now.getFullYear();
  const options = { month: 'short', day: 'numeric' };
  if (isDifferentYear) options.year = 'numeric';
  return date.toLocaleDateString('en-US', options);
};

/**
 * Format standard display date (e.g., "February 2, 2026")
 */
export const formatDisplayDate = (dateInput, isKinyarwanda = false) => {
  const date = parseDate(dateInput);
  if (!date) return '';
  return date.toLocaleDateString(isKinyarwanda ? 'rw-RW' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format full date and time (e.g., "Feb 2, 2026, 6:25 PM")
 */
export const formatDateTime = (dateInput, isKinyarwanda = false) => {
  const date = parseDate(dateInput);
  if (!date) return '';
  return date.toLocaleDateString(isKinyarwanda ? 'rw-RW' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export default {
  parseDate,
  getTimeAgo,
  getTimeAgoShort,
  formatDisplayDate,
  formatDateTime
};
